import { GripVerticalIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import {
  useReportConfigOptions,
  useWizardSelectedSections,
} from '@/modules/analysis'
import { cn } from '@/utils/ui'
import { AvailableSectionItem } from './AvailableSectionItem'
import { DropIndicator } from './DropIndicator'
import { SectionItem } from './SectionItem'
import useDragReorder from './useDragReorder'

export const Sections = () => {
  const config = useReportConfigOptions()
  const sections = config?.sections ?? []
  const selectedSections = useWizardSelectedSections()

  const {
    dropIndex,
    reportListRef,
    reorderableSections,
    startDrag,
    endDrag,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragReorder()

  const availableSections = sections.filter(
    (section) => !selectedSections.includes(section.key) && !section.pinned,
  )

  return (
    <div className="space-y-6" onDragEnd={endDrag}>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div
          className="lg:col-span-3 space-y-2"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <h3 className="text-[13px] font-medium text-ink-subtle">
            Your report
          </h3>
          <div
            className={cn(
              'rounded-lg border bg-white min-h-50 transition-colors',
              dropIndex !== null
                ? 'border-brand bg-brand/5 ring-1 ring-brand'
                : 'border-border',
            )}
          >
            {config?.pinnedTopSections.map((section) => (
              <SectionItem key={section.key} section={section} isPinned />
            ))}

            <div ref={reportListRef} className="relative">
              <AnimatePresence initial={false}>
                {reorderableSections.map((section) => (
                  <motion.div
                    key={section.key}
                    data-reorder-item={section.key}
                    layout
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <SectionItem section={section} onDragStart={startDrag} />
                  </motion.div>
                ))}
              </AnimatePresence>
              <DropIndicator
                containerRef={reportListRef}
                dropIndex={dropIndex}
              />
            </div>

            {reorderableSections.length === 0 && (
              <div
                className={cn(
                  'flex flex-col items-center justify-center py-10 gap-2 border-2 border-dashed rounded-md mx-3 my-3',
                  dropIndex !== null ? 'border-brand/50' : 'border-border/50',
                )}
              >
                <GripVerticalIcon className="h-6 w-6 text-muted-foreground/30" />
                <p className="text-sm font-medium text-muted-foreground/60">
                  Drag sections here to add them to your report
                </p>
              </div>
            )}

            {config?.pinnedBottomSections.map((section) => (
              <SectionItem key={section.key} section={section} isPinned />
            ))}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-2">
          <h4 className="text-[13px] font-medium text-ink-subtle">
            Available sections
          </h4>
          <div className="space-y-1.5 min-h-50">
            <AnimatePresence initial={false}>
              {availableSections.length === 0 ? (
                <motion.p
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-xs text-muted-foreground/60 py-8 text-center"
                >
                  All sections added to report
                </motion.p>
              ) : (
                availableSections.map((section) => (
                  <motion.div
                    key={section.key}
                    layout
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <AvailableSectionItem
                      section={section}
                      onDragStart={startDrag}
                    />
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}
